import Cookies from "universal-cookie";

const cookies = new Cookies();

export const ACCESS_TOKEN_KEY = "access_token";
export const REFRESH_TOKEN_KEY = "refresh_token";
export const TOKEN_EXPIRES_AT_KEY = "token_expires_at";
export const API_BASE_URL_KEY = "api_base_url";
export const ORGANIZATION_KEY = "organization";
export const REFRESH_LOCK_KEY = "oauth_refresh_lock";

/** Keep session metadata alive as long as the refresh token (7 days). */
export const SESSION_COOKIE_MAX_AGE = 604800;

/** Proactive refresh window before access-token expiry. */
export const ACCESS_TOKEN_REFRESH_BUFFER_SECONDS = 300;

export const REFRESH_LOCK_TTL_MS = 10000;

const cookieOptions = (maxAge) => ({
  path: "/",
  sameSite: "strict",
  secure: window.location.protocol === "https:",
  ...(maxAge ? { maxAge } : {}),
});

export const sessionCookieOptions = () => cookieOptions(SESSION_COOKIE_MAX_AGE);

function readLocal(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeLocal(key, value) {
  try {
    if (value == null || value === "") {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // Ignore quota / private-mode failures.
  }
}

/**
 * Shared session storage for the SPA.
 * Standalone HTML forms mirror these same cookie/localStorage keys in
 * public/user-forms/utils/auth.js — keep both sides in sync.
 */
class TokenStorage {
  static setTokens(oauthToken) {
    if (!oauthToken?.access_token) {
      return;
    }

    const expiresInSeconds = Number(oauthToken.expires_in);
    const maxAge = expiresInSeconds > 0 ? expiresInSeconds : undefined;

    cookies.set(ACCESS_TOKEN_KEY, oauthToken.access_token, cookieOptions(maxAge));
    cookies.set(
      TOKEN_EXPIRES_AT_KEY,
      String(Date.now() + expiresInSeconds * 1000),
      cookieOptions(maxAge)
    );

    if (oauthToken.refresh_token) {
      cookies.set(
        REFRESH_TOKEN_KEY,
        oauthToken.refresh_token,
        sessionCookieOptions()
      );
    }
  }

  static getAccessToken() {
    return cookies.get(ACCESS_TOKEN_KEY) || "";
  }

  static getRefreshToken() {
    return cookies.get(REFRESH_TOKEN_KEY) || "";
  }

  static clearTokens() {
    const options = { path: "/" };
    cookies.remove(ACCESS_TOKEN_KEY, options);
    cookies.remove(REFRESH_TOKEN_KEY, options);
    cookies.remove(TOKEN_EXPIRES_AT_KEY, options);
  }

  static isAccessTokenExpired(bufferSeconds = 60) {
    const expiresAt = cookies.get(TOKEN_EXPIRES_AT_KEY);
    if (!expiresAt) {
      return true;
    }
    return Date.now() >= Number(expiresAt) - bufferSeconds * 1000;
  }

  static hasUsableAccessToken() {
    return Boolean(this.getAccessToken()) && !this.isAccessTokenExpired(0);
  }

  static shouldRefreshAccessToken() {
    if (!this.getRefreshToken()) {
      return false;
    }
    return (
      !this.getAccessToken() ||
      this.isAccessTokenExpired(ACCESS_TOKEN_REFRESH_BUFFER_SECONDS)
    );
  }

  /**
   * API base URL for standalone HTML forms (and refresh fallbacks).
   * Durable cookie + localStorage so Safari tab suspension does not wipe it.
   */
  static setApiBaseUrl(apiBaseUrl) {
    if (!apiBaseUrl) {
      return;
    }
    cookies.set(API_BASE_URL_KEY, apiBaseUrl, sessionCookieOptions());
    writeLocal(API_BASE_URL_KEY, apiBaseUrl);
  }

  static getApiBaseUrl() {
    return cookies.get(API_BASE_URL_KEY) || readLocal(API_BASE_URL_KEY) || "";
  }

  static setOrganization(organization) {
    if (!organization) {
      return;
    }
    cookies.set(ORGANIZATION_KEY, organization, sessionCookieOptions());
  }

  static getOrganization() {
    return cookies.get(ORGANIZATION_KEY) || "";
  }

  /**
   * Runtime config needed by both the SPA and HTML forms.
   * Safe to call on every SPA boot and after login.
   */
  static ensureClientConfig({ apiBaseUrl, organization } = {}) {
    if (apiBaseUrl) {
      this.setApiBaseUrl(apiBaseUrl);
    } else if (process.env.REACT_APP_API_BASE_URL) {
      this.setApiBaseUrl(process.env.REACT_APP_API_BASE_URL);
    }

    if (organization) {
      this.setOrganization(organization);
    }
  }
}

export default TokenStorage;
